import { useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  GetItWalletAdjustmentPayload,
  GetItSubscriptionResponse,
  GetItSubscriptionDetailsResponse,
  GetItDeliveryStatusResponse,
  UseGetItOptions,
} from '../types';

const useGetIt = (options: UseGetItOptions = {}) => {
  const { id } = useParams();
  const userId = id as string;
  const queryClient = useQueryClient();
  const [isPerformingAction, setIsPerformingAction] = useState<boolean>(false);

  // Queries
  const subscriptionsQuery = useQuery({
    queryKey: ['getit-subscriptions', userId, options.page, options.limit, options.status],
    queryFn: () =>
      httpClient
        .get<GetItSubscriptionResponse>(
          `/admin/getit/users/${userId}/subscriptions?page=${options.page || 1}&limit=${options.limit || 20}&status=${
            options.status || ''
          }`,
        )
        .then((res) => res.data),
    enabled: !!userId && options.enabled !== false && !options.subId,
  });

  const detailsQuery = useQuery({
    queryKey: ['getit-details', userId, options.subId],
    queryFn: () =>
      httpClient
        .get<GetItSubscriptionDetailsResponse>(`/admin/getit/users/${userId}/subscriptions/${options.subId}`)
        .then((res) => res.data.data),
    enabled: !!userId && !!options.subId,
  });

  const deliveryQuery = useQuery({
    queryKey: ['getit-delivery', userId, options.subId],
    queryFn: () =>
      httpClient
        .get<GetItDeliveryStatusResponse>(`/admin/getit/users/${userId}/subscriptions/${options.subId}/delivery-status`)
        .then((res) => res.data.data),
    enabled: !!userId && !!options.subId,
  });

  // Mutations/Actions
  const reinitiateAutosave = async (userId: string, subId: string) => {
    try {
      setIsPerformingAction(true);
      const url = `/admin/getit/users/${userId}/subscriptions/${subId}/reinitiate-autosave`;
      const response = await httpClient.post(url);
      if (response?.data?.status) {
        Alert.success(response.data.message || 'Autosave reinitiated successfully');
        queryClient.invalidateQueries({ queryKey: ['getit-details', userId, subId] });
      }
      return response;
    } catch (error: any) {
      console.error('Error reinitiating autosave:', error);
      Alert.error(error?.response?.data?.message || 'Error reinitiating autosave');
      return error;
    } finally {
      setIsPerformingAction(false);
    }
  };

  const refundUndeliveredSubscription = async (subId: string, callback?: () => void) => {
    try {
      setIsPerformingAction(true);
      const url = `/admin/getit/refund-undelivered/subscriptions/${subId}`;
      const response = await httpClient.post(url);
      if (response?.data?.status) {
        Alert.success(response.data.message || 'Refund approved successfully');
        queryClient.invalidateQueries({ queryKey: ['getit-details', userId, subId] });
        queryClient.invalidateQueries({ queryKey: ['getit-subscriptions', userId] });
        if (callback) callback();
      }
      return response;
    } catch (error: any) {
      console.error('Error approving refund:', error);
      Alert.error(error?.response?.data?.message || 'Error approving refund');
      return error;
    } finally {
      setIsPerformingAction(false);
    }
  };

  const adjustSubscriptionWallet = async (
    userId: string,
    subId: string,
    payload: GetItWalletAdjustmentPayload,
    callback?: () => void,
  ) => {
    try {
      setIsPerformingAction(true);
      const url = `/admin/getit/users/${userId}/subscriptions/${subId}/wallet/adjust`;
      const response = await httpClient.post<any>(url, payload);
      if (response?.data?.status) {
        Alert.success(response.data.message || 'Wallet adjusted successfully');
        queryClient.invalidateQueries({ queryKey: ['getit-details', userId, subId] });
        if (callback) callback();
      }
      return response;
    } catch (error: any) {
      console.error('Error adjusting GetIt wallet:', error);
      Alert.error(error?.response?.data?.message || 'Error adjusting wallet');
      return error;
    } finally {
      setIsPerformingAction(false);
    }
  };

  return {
    // States from queries
    isLoading: subscriptionsQuery.isLoading,
    fetchingDetails: detailsQuery.isLoading,
    fetchingDelivery: deliveryQuery.isLoading,
    isPerformingAction,

    // Data from queries
    subscriptions: subscriptionsQuery.data?.data?.subscriptions || [],
    subscriptionDetails: detailsQuery.data || null,
    deliveryStatus: deliveryQuery.data || null,
    pagination: subscriptionsQuery.data?.data?.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
    },

    // Queries directly (for refetching etc)
    subscriptionsQuery,
    detailsQuery,
    deliveryQuery,

    // Actions
    reinitiateAutosave,
    adjustSubscriptionWallet,
    refundUndeliveredSubscription,
  };
};

export default useGetIt;
